import type { NormalizedApiError } from '@/api/errors';

/**
 * One voice for refusals on the outbound-webhook surface: the UI only ever offers what the API
 * serves, and when the API refuses, the refusal is EXPLAINED (which key, which roles, which
 * lane), never masked. Copy mirrors `platform-security/.../permission-matrix.yaml` and the
 * PublishModal step-up wording.
 */

/** Explain a refused subscription WRITE (create/update/rotate/pause/resume/test). */
export function explainWriteRefusal(e: NormalizedApiError): string {
  if (e.httpStatus === 403 && e.code === 'STEPUP_REQUIRED') {
    return (
      'Writing subscriptions is a step-up lane (subscriptions.write): re-authenticate with a ' +
      'second factor (loa2) and retry.'
    );
  }
  if (e.httpStatus === 403) {
    return (
      "Your role can't write subscriptions — subscriptions.write is held by hub_admin only " +
      '(it mints a signing secret and redirects tenant data outward). The server refused; ' +
      'this screen hides nothing extra.'
    );
  }
  if (e.code === 'WEBHOOKS_NOT_CONFIGURED') {
    return (
      'The webhook surface is not configured in this deployment (no signing master key), so ' +
      'every write refuses honestly. Reading existing subscriptions still works.'
    );
  }
  // WEBHOOK_URL_INVALID / WEBHOOK_TYPE_UNKNOWN / WEBHOOK_LIMIT_EXCEEDED / WEBHOOK_STATE_CONFLICT
  // and anything else: the server message is already the explanation — surface it verbatim.
  return e.message;
}

/** Explain a refused delivery-ledger READ. */
export function explainDeliveriesReadRefusal(e: NormalizedApiError): string {
  if (e.httpStatus === 403) {
    return (
      "Your role can't read the delivery ledger — subscriptions.deliveries.read is held by " +
      'hub_admin, hub_operator and auditor.'
    );
  }
  return e.message;
}

/** Explain a refused REPLAY. */
export function explainReplayRefusal(e: NormalizedApiError): string {
  if (e.httpStatus === 403) {
    return (
      "Your role can't replay deliveries — subscriptions.deliveries.replay is held by " +
      'hub_admin only.'
    );
  }
  // 409 WEBHOOK_STATE_CONFLICT ("only DEAD rows replay") surfaces verbatim.
  return e.message;
}
